import { colors } from '@/constants/colors'
import type { WeekView } from '@/features/scheduling/availability'
import { countOnDay, weekDays } from '@/features/scheduling/availability'
import { Pressable } from '@/ui/core/pressable'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { format, parseISO } from 'date-fns'
import { View } from 'react-native'

/**
 * The week's seven days, and which of them hold anything.
 *
 * Picking a day before a studio is the order the question is actually asked in: a member knows
 * roughly when they can come and then asks who can see them, where listing every studio's whole
 * week asks them to read thirty-five cells to find out.
 *
 * Every date is in ink, and every day carries a mark under it: a green dot where the studios are
 * open, a hollow ring where none of them are. Nothing is left to say itself by absence — a day
 * with no fill and no mark is a day a member cannot tell apart from one they simply have not
 * looked at, and tapping it does nothing. Green because this app already means "well" by it.
 *
 * No count. How many is a fact about the day worth knowing only once the day is chosen, and the
 * studios underneath are where it is answered exactly.
 */

/**
 * Whether a day is open, told under its date.
 *
 * Both states are the same shape at the same weight — a filled dot — and only the colour says
 * which. A hollow ring was a different object as well as a different colour, and at six points
 * a hairline is the first thing a screen loses to glare.
 *
 * The shut dot is the edge token — the hairline every bordered thing in this app is drawn with,
 * so the mark is the border tone rather than a colour of its own and nothing new enters the
 * palette. Warm, where the muted grey it replaced was cold: muted was written for letters, which
 * have strokes to carry a tone, and a six-point disc has none.
 *
 * Light on purpose. Most days in most weeks are shut, so this dot is the one drawn most often —
 * at full strength it was the heaviest thing in the card after the chosen cell, and a strip
 * where the loudest mark means "nothing here" has its emphasis backwards. Present when looked
 * at, gone when scanned past, which is the whole job.
 *
 * The chosen day's dots are drawn for ink: mint where it is open, white where it is not, since
 * leaf and grey both disappear into black. A shut day can be the chosen one — it is tappable
 * like any other — so both have to survive the fill.
 */
const Mark = ({ open, isChosen }: { open: boolean; isChosen: boolean }) => (
	<View className="h-2 items-center justify-center">
		<View
			className="h-1.5 w-1.5 rounded-full"
			style={{
				backgroundColor: open
					? isChosen
						? colors.mint
						: colors.leaf
					: isChosen
					  ? 'rgba(255,255,255,0.6)'
					  : colors.edge,
			}}
		/>
	</View>
)
export const DayStrip = ({
	view,
	chosen,
	onChoose,
}: { view: WeekView; chosen?: string; onChoose: (date: string) => void }) => (
	<HStack gap={5}>
		{weekDays(view.weekStart).map((date) => {
			const count = countOnDay(view, date)
			const day = parseISO(`${date}T00:00:00`)
			const isChosen = date === chosen
			const open = count > 0

			return (
				/*
				 * Only the days with something take a tap.
				 *
				 * The dot is what makes this readable rather than dead: green where a day is
				 * open, grey where it is not, on all seven — so a finger knows before it lands,
				 * and the four cells in seven that refuse it are the four that said they would.
				 * Without a mark of that kind this is the disabled arrow's problem at seven times
				 * the size, which is why the dots came first.
				 *
				 * A shut day is still reachable from the calendar, which knows the booking window
				 * and not what is free inside it — so the empty-day state underneath stays.
				 */
				<Pressable
					key={date}
					onPress={() => onChoose(date)}
					disabled={!open}
					accessibilityRole="button"
					accessibilityState={{ selected: isChosen, disabled: !open }}
					accessibilityLabel={`${format(day, 'EEEE d MMMM')}, ${
						open ? `${count} ${count === 1 ? 'time' : 'times'}` : 'nothing open'
					}`}
					className="flex-1"
					haptic={open}
				>
					<VStack
						gap={2}
						className="items-center rounded-brand py-2"
						// Blush for a day that holds something, ink for the one chosen, nothing for a
						// quiet day. White is what the card behind them is, so the fill that used to
						// mark an open day on the pink says nothing here.
						style={{
							backgroundColor: isChosen
								? colors.ink
								: open
								  ? colors.blush
								  : 'transparent',
						}}
					>
						{/* A day with nothing is drawn entirely in the hairline tone — the same one its
						    dot is in — so the whole cell recedes as one thing rather than a grey
						    date over a lighter mark. Washed out rather than merely dimmer: at the
						    app's muted grey a shut day still read as type to be gone back to, when
						    what it is is a cell that will not answer. A disabled control is the one
						    place contrast may go this far, since there is nothing there to read.
						    
						    The whole argument for ink on all seven was that the fill and the dot
						    carried availability between them, and that was before a cell could turn
						    a finger away. */}
						<Text
							size="body3"
							tone={isChosen ? 'white' : undefined}
							style={open ? undefined : { color: colors.edge }}
						>
							{format(day, 'EEE')}
						</Text>
						{/* The date at body0. It is the one thing on this strip a member has to
						    read, and at body1 it was set at the same size as a caption. */}
						<Text
							size="body0"
							weight="bold"
							tone={isChosen ? 'white' : undefined}
							style={open ? undefined : { color: colors.edge }}
						>
							{format(day, 'd')}
						</Text>
						<Mark open={open} isChosen={isChosen} />
					</VStack>
				</Pressable>
			)
		})}
	</HStack>
)
